// Sentry telemetry wrapper.
//
// The @sentry/react SDK is loaded via dynamic import() so it stays out of the
// eager entry bundle and never blocks first paint. When disabled the SDK is
// never fetched. Exported functions keep synchronous void signatures; calls
// made before the SDK loads are queued and replayed once it's ready.
type SentryModule = typeof import("@sentry/react");

const ENABLED = import.meta.env.VITE_SENTRY_ENABLED === "true";
const DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || "development";
const RELEASE = import.meta.env.VITE_SENTRY_RELEASE || undefined;
const TRACES_SAMPLE_RATE = Number(
  import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || "0"
);

let sentry: SentryModule | null = null;
let initStarted = false;

/** Calls queued while the SDK is still loading, replayed in order once ready. */
const pendingQueue: Array<(s: SentryModule) => void> = [];

function enqueue(fn: (s: SentryModule) => void): void {
  if (sentry) {
    fn(sentry);
    return;
  }
  if (!initStarted) return;
  pendingQueue.push(fn);
}

function flushQueue(): void {
  if (!sentry) return;
  while (pendingQueue.length) {
    const fn = pendingQueue.shift();
    try {
      fn?.(sentry);
    } catch (e) {
      console.error("[sentry] queued call failed:", e);
    }
  }
}

export function initSentry(): void {
  if (initStarted) return;
  if (!ENABLED || !DSN) {
    console.log(`[sentry] disabled (enabled=${ENABLED}, hasDsn=${!!DSN})`);
    return;
  }
  initStarted = true;
  // Deferred load — keeps @sentry/react out of the critical-path bundle.
  import("@sentry/react")
    .then((Sentry) => {
      Sentry.init({
        dsn: DSN,
        environment: ENVIRONMENT,
        release: RELEASE,
        tracesSampleRate: Number.isFinite(TRACES_SAMPLE_RATE)
          ? TRACES_SAMPLE_RATE
          : 0,
        integrations: [Sentry.browserTracingIntegration()],
      });
      sentry = Sentry;
      console.log(`[sentry] enabled (environment=${ENVIRONMENT})`);
      flushQueue();
    })
    .catch((e) => {
      console.error("[sentry] failed to load:", e);
    });
}

export function identifyUser(
  userId: string,
  organizationId: string | undefined,
  role: string,
  analyticsEnabled: boolean
): void {
  enqueue((Sentry) => {
    if (!analyticsEnabled) {
      Sentry.setUser(null);
      return;
    }
    Sentry.setUser({ id: userId });
    Sentry.setTag("role", role);
    if (organizationId) Sentry.setTag("organization", organizationId);
  });
}

export function resetUser(): void {
  enqueue((Sentry) => Sentry.setUser(null));
}

export function captureException(
  err: Error,
  extra?: Record<string, unknown>
): void {
  enqueue((Sentry) => {
    try {
      Sentry.withScope((scope) => {
        if (extra) {
          for (const [k, v] of Object.entries(extra)) scope.setExtra(k, v);
        }
        Sentry.captureException(err);
      });
    } catch (e) {
      console.error("[sentry] captureException failed:", e);
    }
  });
}

export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>,
  level: "info" | "warning" | "error" = "info"
): void {
  enqueue((Sentry) => {
    try {
      Sentry.addBreadcrumb({
        category,
        message,
        level,
        data,
        timestamp: Date.now() / 1000,
      });
    } catch (e) {
      console.error("[sentry] addBreadcrumb failed:", e);
    }
  });
}

export function isSentryEnabled(): boolean {
  return sentry !== null;
}
